import { useId, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Portal from "@mui/material/Portal";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { formatMoney as formatMoneyShared } from "@bw/shared/utils";

import {
  cancelEnrollment,
  getUserDisplayName,
  listTrackEnrollments,
  type ProgramTrackResponse,
  type UnifiedEnrollmentResponse,
} from "@/libs/programsApi";
import { formatDate } from "./program-detail-time";

const cancelSchema = z.object({ reason: z.string() });
type CancelValues = z.infer<typeof cancelSchema>;

const ENROLLMENT_STATUS: Record<
  string,
  {
    color: "default" | "error" | "info" | "success" | "warning";
    label: string;
  }
> = {
  enrolled: { color: "success", label: "Enrolled" },
  waitlisted: { color: "warning", label: "Waitlisted" },
  promoted: { color: "info", label: "Promoted" },
  cancelled: { color: "error", label: "Cancelled" },
  promotion_expired: { color: "default", label: "Promotion expired" },
};

type Feedback = {
  message: string;
  severity: "error" | "success";
};

function humanizeEnrollmentStatus(status: string): string {
  const words = (status || "").replace(/_/g, " ").trim();
  if (!words) return "—";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function RosterSection({
  track,
  onCancelled,
}: {
  track: ProgramTrackResponse;
  onCancelled: () => Promise<void> | void;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const headingId = useId();
  const trackId = track.track_id;
  const [pendingCancel, setPendingCancel] =
    useState<UnifiedEnrollmentResponse | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const cancelInFlight = useRef(false);

  const rosterQuery = useQuery({
    queryKey: ["programs", "tracks", trackId, "enrollments"],
    queryFn: () => listTrackEnrollments(trackId),
    retry: false,
  });
  const enrollments = rosterQuery.data ?? [];
  const studentIds = useMemo(
    () => Array.from(new Set(enrollments.map((row) => row.student_id))),
    [enrollments],
  );
  const nameQueries = useQueries({
    queries: studentIds.map((studentId) => ({
      queryKey: ["programs", "student-display-name", studentId],
      queryFn: () => getUserDisplayName(studentId),
      retry: false,
    })),
  });
  const names = useMemo(
    () =>
      Object.fromEntries(
        studentIds.map((studentId, index) => [
          studentId,
          nameQueries[index]?.data ?? "—",
        ]),
      ),
    [nameQueries, studentIds],
  );

  const { handleSubmit, register, reset } = useForm<CancelValues>({
    defaultValues: { reason: "" },
    resolver: zodResolver(cancelSchema),
  });

  const cancelMutation = useMutation({
    mutationFn: ({
      enrollmentId,
      reason,
    }: {
      enrollmentId: string;
      reason?: string;
    }) => cancelEnrollment(enrollmentId, reason),
  });

  const studentLabel = (studentId: string): string =>
    names[studentId] ?? "—";

  const openCancel = (enrollment: UnifiedEnrollmentResponse) => {
    setPendingCancel(enrollment);
    reset({ reason: "" });
  };

  const closeCancel = () => {
    if (cancelMutation.isPending) return;
    setPendingCancel(null);
    reset({ reason: "" });
  };

  const confirmCancel = handleSubmit(async ({ reason }) => {
    if (cancelInFlight.current || !pendingCancel) return;
    cancelInFlight.current = true;
    try {
      await cancelMutation.mutateAsync({
        enrollmentId: pendingCancel.enrollment_id,
        reason: reason.trim() || undefined,
      });
      await Promise.all([
        rosterQuery.refetch({ throwOnError: true }),
        onCancelled(),
      ]);
      setFeedback({ severity: "success", message: "Enrollment cancelled" });
      setPendingCancel(null);
      reset({ reason: "" });
    } catch (error) {
      setFeedback({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to cancel this enrollment.",
      });
    } finally {
      cancelInFlight.current = false;
    }
  });

  const statusChip = (enrollment: UnifiedEnrollmentResponse) => {
    const metadata = ENROLLMENT_STATUS[enrollment.status] ?? {
      color: "default" as const,
      label: humanizeEnrollmentStatus(enrollment.status),
    };
    return (
      <Chip
        color={metadata.color}
        label={metadata.label}
        size="small"
        variant="outlined"
      />
    );
  };

  const cancelButton = (enrollment: UnifiedEnrollmentResponse) =>
    enrollment.status === "cancelled" ? null : (
      <Button
        onClick={() => openCancel(enrollment)}
        size="small"
        variant="outlined"
      >
        Cancel enrollment
      </Button>
    );

  return (
    <Box
      aria-label="Track roster"
      aria-labelledby={headingId}
      component="section"
      sx={{ py: 1 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography component="h3" id={headingId} variant="subtitle1">
          Roster — {track.name}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {track.enrolled_count} / {track.max_students} enrolled ·{" "}
          {track.waitlist_count} waitlisted
        </Typography>
      </Stack>

      {rosterQuery.isPending ? (
        <Stack spacing={1}>
          <Skeleton height={32} variant="rounded" width="35%" />
          <Skeleton height={44} variant="rounded" />
          <Skeleton height={44} variant="rounded" width="75%" />
        </Stack>
      ) : rosterQuery.isError ? (
        <Alert severity="error">
          We couldn&apos;t load the roster for this track. Please try again.
        </Alert>
      ) : enrollments.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3 }}>
          No one enrolled yet.
        </Typography>
      ) : isDesktop ? (
        <TableContainer>
          <Table aria-label={`${track.name} roster`} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Player</TableCell>
                <TableCell>Enrolled on</TableCell>
                <TableCell>Fee paid</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((enrollment) => (
                <TableRow key={enrollment.enrollment_id}>
                  <TableCell>{studentLabel(enrollment.student_id)}</TableCell>
                  <TableCell>{formatDate(enrollment.enrolled_at, "UTC")}</TableCell>
                  <TableCell>
                    {formatMoneyShared(
                      enrollment.fee_paid,
                      enrollment.currency,
                    )}
                  </TableCell>
                  <TableCell>{statusChip(enrollment)}</TableCell>
                  <TableCell align="right">
                    {cancelButton(enrollment)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <List aria-label={`${track.name} roster`} disablePadding>
          {enrollments.map((enrollment) => (
            <ListItem
              divider
              key={enrollment.enrollment_id}
              sx={{ alignItems: "flex-start", px: 0, py: 1.5 }}
            >
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }} variant="body2">
                      {studentLabel(enrollment.student_id)}
                    </Typography>
                    {statusChip(enrollment)}
                  </Stack>
                }
                secondary={
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Enrolled {formatDate(enrollment.enrolled_at, "UTC")} ·{" "}
                      {formatMoneyShared(
                        enrollment.fee_paid,
                        enrollment.currency,
                      )}
                    </Typography>
                    <Box>{cancelButton(enrollment)}</Box>
                  </Stack>
                }
                slotProps={{ secondary: { component: "div" } }}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        aria-labelledby="cancel-enrollment-title"
        fullWidth
        maxWidth="sm"
        onClose={closeCancel}
        open={pendingCancel !== null}
      >
        <Box component="form" onSubmit={confirmCancel}>
          <DialogTitle id="cancel-enrollment-title">
            Cancel this enrollment?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {pendingCancel
                ? `${studentLabel(pendingCancel.student_id)} will be removed from “${track.name}”. Any refund and waitlist promotion are handled automatically.`
                : ""}
            </DialogContentText>
            {feedback?.severity === "error" ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {feedback.message}
              </Alert>
            ) : null}
            <TextField
              fullWidth
              id="enrollment-cancel-reason"
              label="Reason (optional)"
              multiline
              placeholder="Why is this enrollment being cancelled?"
              rows={3}
              {...register("reason")}
            />
          </DialogContent>
          <DialogActions>
            <Button
              disabled={cancelMutation.isPending}
              onClick={closeCancel}
              size="small"
            >
              Keep enrollment
            </Button>
            <Button
              color="error"
              disabled={cancelMutation.isPending}
              size="small"
              type="submit"
              variant="contained"
            >
              {cancelMutation.isPending ? "Cancelling..." : "Cancel enrollment"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Portal>
        <Snackbar
          autoHideDuration={5000}
          key={feedback?.message}
          onClose={() => setFeedback(null)}
          open={feedback !== null}
        >
          <Alert
            onClose={() => setFeedback(null)}
            severity={feedback?.severity ?? "success"}
            variant="filled"
          >
            {feedback?.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Box>
  );
}
